import type { XlsxRow } from '/shared/functions/getFromXlsx';
import type { ReactElement, ReactNode } from 'react';
import type {
  BasketTableAnswerItem,
  QuestionDataObject,
  QuestionConfigBasketStructureType,
  QuestionConfigBranchLoader,
  QuestionConfigFormComponent,
  QuestionConfigFormProps,
  QuestionConfigHookLoader,
  QuestionConfigQuestionType,
} from '/index';
import type { QuestionBranchName } from '/shared/util/Statics';

type UnknownRecordValue =
  | ReactNode
  | object
  | UnknownRecordValue[]
  | ((...args: never[]) => string | number | boolean | object | null | undefined);

export type UnknownRecord = Record<string, UnknownRecordValue>;

export type QuestionFormProps = QuestionConfigFormProps;

export type QuestionFormComponent = QuestionConfigFormComponent;
export type LazyQuestionFormModule = Promise<{ default: QuestionFormComponent }>;
export type QuestionHookLoader = QuestionConfigHookLoader;
export type QuestionBranchLoader = QuestionConfigBranchLoader;

export type QuestionBranchSelector = {
  injectQuestionBranchSelector?: boolean;
  injectSummaryView?: boolean;
  isActivityBranchNamedSpendData?: boolean;
  options?: {
    description?: string;
    requiredData?: string;
    title: string;
  }[];
  para?: ReactElement | string;
  questionBaseRootApiKey?: string;
  questionBranchEnableSupplierData?: boolean;
  questionBranchEnableCustomData?: boolean;
  tableTabs?: {
    label: string;
    apiKey: string;
    emptyLabel?: string;
  }[];
  tableViewSummaryHeadings?: string[];
  tableViewSummaryMeta?: {
    bivarianceHack(items: BasketTableAnswerItem[]): string;
  }['bivarianceHack'];
  tableViewSummaryRowParser?: {
    bivarianceHack(items: BasketTableAnswerItem[]): BasketTableAnswerItem[];
  }['bivarianceHack'];
};

export type QuestionTreeItem = UnknownRecord & {
  id?: string;
  apiKey?: string;
  title?: string;
  primaryI18nKey?: string;
  subTextI18nKey?: string;
  breadcrumbTitle?: string;
  cta?: {
    title: string;
  };
  description?: ReactNode;
  descriptionDisclaimer?: ReactNode;
  editModeBreadcrumbTitle?: string;
  exclusiveWith?: string[];
  exclusiveWithLabel?: string;
  enlargeIcon?: boolean;
  hideParentBreadcrumb?: boolean;
  icon?: string;
  removeBreadcrumbLink?: boolean;
  path?: string[] | string;
  scopeCategory?: string;
  type?: string;
  depth?: number;
  selector?: QuestionBranchSelector;
  groups?: QuestionTreeItem[];
  questions?: QuestionTreeItem[];
  subQuestions?: QuestionTreeItem[];
  children?: QuestionTreeItem[];
  parent?: QuestionTreeItem | null;
  numOfChildren?: number;
  flattened?: boolean;
  hasFilledBackground?: boolean;
  hasUnlockStep2Button?: boolean;
  helper?: React.ReactNode;
  infoModalName?: string;
  table?: QuestionConfigBasketStructureType;
  details?: {
    title?: string;
  };
  editPageTitle?: string;
  education?: QuestionConfigQuestionType['education'];
  form?: LazyQuestionFormModule | QuestionFormComponent;
  freeTypeDistance?: boolean;
  groupIntro?: ReactNode;
  hasSidebar?: boolean;
  hideSidebar?: boolean;
  initiative?: QuestionConfigQuestionType['initiative'];
  isSelectorPage?: boolean;
  overviewPageTitleI18nKey?: string;
  redirect?: (props: { applicationId: number | null }) => string;
  removeProvidedPadding?: boolean;
  supplierAndCustomSubheader?: ReactElement;
  displayLesseeField?: boolean;
  displayNumOfVehicles?: boolean;
  enforceSidebarSpace?: boolean;
  sidebarShort?: boolean;
  useHookLoader?: QuestionHookLoader;
  useQuestionBranchCustomLoader?: QuestionBranchLoader;
  useQuestionBranchSupplierLoader?: QuestionBranchLoader;
};

export type DecoratedQuestionTreeItem = Omit<
  QuestionTreeItem,
  'children' | 'depth' | 'groups' | 'numOfChildren' | 'parent' | 'path' | 'questions' | 'subQuestions' | 'type'
> & {
  children: DecoratedQuestionTreeItem[];
  depth: number;
  groups: DecoratedQuestionTreeItem[];
  hasUnlockStep2Button?: boolean;
  helper?: React.ReactNode;
  id?: string;
  infoModalName?: string;
  numOfChildren: number;
  parent: DecoratedQuestionTreeItem | null;
  path: string[];
  questions: DecoratedQuestionTreeItem[];
  subQuestions: DecoratedQuestionTreeItem[];
  title?: string;
  type: string;
};

export type QuestionCollection = Array<QuestionTreeItem | null | undefined>;

export type RouteQuestionParams = {
  section?: string;
  group?: string;
  potentialActivityBranch?: QuestionBranchName | null;
  question?: string;
  subQuestion?: string;
};

export type AnswerData = UnknownRecord & {
  type?: string | string[];
  itemId?: string | number | null;
  genericData?: UnknownRecord & {
    base_question_key?: string;
    hasNoData?: boolean;
  };
  calculationData?: UnknownRecord | null;
  tonnage?: number | false;
  harmonisedUnit?: string | null;
  harmonisedValue?: number | null;
};

export type AnswerForQuestion = {
  question: QuestionTreeItem;
  answer: AnswerData;
  isNeo: boolean;
};

export type TableCellFormatter<Data = QuestionDataObject> = {
  bivarianceHack(tableData: Data): ReactElement | string | number | null | undefined;
}['bivarianceHack'];

export type TableCell = string | TableCellFormatter | null;

export type AssociatedUser = {
  id?: number | string;
  role?: string | null;
  invite_state?: string;
  email?: string | null;
  user?: {
    id?: number;
    firstName?: string | null;
    lastName?: string | null;
    telephone?: string | null;
  } | null;
};

export type ValidationErrorInput = {
  message: string;
  code?: string;
  element?: string;
};

export type ValidationDispatch = (action: { type: string; error: Error }) => void;

export type BulkUploadRef = {
  current: {
    getSelectedFile: () => Blob | null;
  };
};

export type XlsxCallback<Result> = (error: string | Error | null, rows: Result[]) => Result[] | string | null;

export const getXlsxString = (row: XlsxRow, key: string): string => {
  const value = row[key];
  if (value === null || value === undefined) {
    return '';
  }
  return value.toString();
};
